import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';

const WS_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8009')
  .replace(/^http/, 'ws');

// Sent by the backend's /ws endpoint when the token fails to decode or is expired —
// see app/main.py. Retrying with the same dead token is pointless; force a re-login
// instead of silently exhausting reconnect attempts and going dark.
const WS_CLOSE_AUTH_FAILED = 4001;

export interface ScanStatusEvent {
  event: 'scan_status';
  scan_id: number;
  status: string;
  message: string;
}

export interface ScanCompleteEvent {
  event: 'scan_complete';
  scan_id: number;
  repo_id: number;
  debt_score: number;
  issue_count: number;
  savings_headline: string | null;
}

export interface FixStartedEvent {
  event: 'fix_started';
  scan_id: number;
  issue_ids: number[];
}

export interface FixCompleteEvent {
  event: 'fix_complete';
  scan_id: number;
  pr_url: string;
  pr_number: number;
  fixed_issue_ids: number[];
  skipped_files: string[];
}

export interface FixFailedEvent {
  event: 'fix_failed';
  scan_id: number;
  error: string;
}

export interface AgentStepEvent {
  event: 'agent_step';
  plan_id: number;
  phase: 'plan' | 'pr' | 'issue' | 'fix';
  step: string;
  status: 'running' | 'done' | 'failed';
  message: string;
  step_index?: number;
  step_total?: number;
}

export interface AgentCompleteEvent {
  event: 'agent_complete';
  plan_id: number;
  phase: 'plan' | 'pr' | 'issue' | 'fix';
  pr_url?: string;
  pr_number?: number;
  issue_url?: string;
  issue_number?: number;
}

export interface AgentFailedEvent {
  event: 'agent_failed';
  plan_id: number;
  phase: 'plan' | 'pr' | 'issue' | 'fix';
  step: string;
  reason: string;
}

export type WsEvent =
  | ScanStatusEvent | ScanCompleteEvent
  | FixStartedEvent | FixCompleteEvent | FixFailedEvent
  | AgentStepEvent | AgentCompleteEvent | AgentFailedEvent;

interface UseWebSocketOptions {
  userId: number | null;
  token: string | null;
  onMessage: (event: WsEvent) => void;
}

/**
 * Connects to /ws?token=<jwt> and delivers typed events.
 * The backend validates the token and derives the user_id — the client never
 * sends a user_id that could be spoofed to subscribe to another user's events.
 * Reconnects automatically on unexpected close (up to 5 times).
 */
export function useWebSocket({ userId, token, onMessage }: UseWebSocketOptions) {
  const dispatch = useDispatch();
  const wsRef = useRef<WebSocket | null>(null);
  const retriesRef = useRef(0);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    if (!userId || !token) return;

    const ws = new WebSocket(`${WS_BASE_URL}/ws?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;

    ws.onmessage = (e) => {
      try {
        const data: WsEvent = JSON.parse(e.data);
        onMessageRef.current(data);
      } catch {
        // ignore malformed frames
      }
    };

    ws.onclose = (e) => {
      if (e.code === WS_CLOSE_AUTH_FAILED) {
        localStorage.removeItem('accessToken');
        dispatch(logout());
        if (!window.location.pathname.startsWith('/auth/')) {
          window.location.href = '/auth/login';
        }
        return;
      }
      if (!e.wasClean && retriesRef.current < 5) {
        retriesRef.current += 1;
        setTimeout(connect, 2000 * retriesRef.current);
      }
    };

    ws.onerror = () => ws.close();
  }, [userId, token, dispatch]);

  useEffect(() => {
    connect();
    return () => {
      wsRef.current?.close(1000, 'unmount');
    };
  }, [connect]);
}
